import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { debounce } from 'lodash'
import actions from 'redux/actions'
import { useState, useRef, useCallback, useOnClickOutside } from 'sb-hooks'
import { constants, mixpanel, snowplow, compose, scrollToElement } from 'common/helpers'
import Collapse from 'react-collapse'

import injectGlobals from 'decorators/injectGlobals'

import WidthContainer from 'components/_new/layout/widthContainer/WidthContainer/WidthContainer'
import SearchSuggestions from 'layouts/InsideLayout/Header/HeaderSearchInput/SearchSuggestions/SearchSuggestions'
import SearchInput from './SearchInput/SearchInput'
import SearchResults from './SearchResults/SearchResults'

import s from './HeaderAlgoliaSearchInput.scss'


const queue = []

const HeaderAlgoliaSearchInput = ({ isMobile }) => {
  const [ isOpened, setIsOpened ] = useState(false)
  const [ isFirstLoaded, setIfFirstLoaded ] = useState(false)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ query, setQuery ] = useState('')
  const rootRef  = useRef(null)
  const inputRef = useRef(null)

  const fetchData = useCallback(debounce((query) => {
    setIsLoading(true)

    const request = actions.models.search.fetchFromAlgolia(query)

    queue.push(request)

    request.finally(() => {
      queue.pop()

      if (!isFirstLoaded) {
        setIfFirstLoaded(true)
      }

      if (queue.length < 1) {
        setIsLoading(false)
      }
    })
  }, 250), [])

  const handleShow = useCallback(() => {
    setIsOpened(true)
    snowplow.track(constants.snowplowEvents.searchInputClick)

    if (isMobile) {
      scrollToElement('searchInput', true)
    }
  }, [])

  const handleHide = useCallback(() => setIsOpened(false), [])

  useOnClickOutside(rootRef, handleHide)

  const sendQueryAnalyticsEvent = useCallback(() => {
    snowplow.track(constants.snowplowEvents.search.query, { query }, false)
  }, [ query ])

  /**
   * @param {Object} options
   * @param {string} options.suggestionType
   * @param {number} options.position
   * @returns {void}
   */
  const handleSuggestionClick = useCallback(({ suggestionType, position }) => {
    const snowplowMetadata = {
      suggestionType,
      position,
    }
    const mixpanelMetadata = {
      placement: `position ${position}`,
    }

    snowplow.track(constants.snowplowEvents.search.suggestionClick, snowplowMetadata)
    handleHide()
  }, [])

  /**
   * @param {Object} options
   * @param {Object} options.product
   * @param {number} options.product.id
   * @param {string} options.product.name
   * @param {boolean} options.product.available
   * @param {number} options.position
   * @return {void}
   */
  const handleProductClick = useCallback((
    { product: { id, name, available, objectID }, position, queryID, indexName }
  ) => {
    const snowplowEvent = available
      ? constants.snowplowEvents.search.nameClick
      : constants.snowplowEvents.search.nameUnavailableClick

    const snowplowMetadata = {
      query,
      productId: id,
      productName: name,
      position,
    }

    const algoliaMetadata = {
      index: indexName,
      eventName: constants.snowplowEvents.search.nameClick,
      queryID,
      objectIDs: [ objectID ],
      positions: [ position ],
    }

    // algoliaAnalytics.clickedObjectIDsAfterSearch(algoliaMetadata)
    snowplow.track(snowplowEvent, snowplowMetadata)
    sendQueryAnalyticsEvent()
    handleHide()
  }, [ query ])

  /**
   * @param {Object} options
   * @param {Object} options.product
   * @param {number} options.product.id
   * @param {string} options.product.name
   * @return {void}
   */
  const handleProductAdd = useCallback(({ product: { id, name, objectID }, product, queryID, indexName }) => {
    snowplow.track(constants.snowplowEvents.addToQueue, {
      placement: 'search bar',
      productId: id,
      productName: name,
    }, false)

    const algoliaMetadata = {
      index: indexName,
      eventName: constants.snowplowEvents.addToQueue,
      queryID,
      objectIDs: [ objectID ],
    }

    // algoliaAnalytics.convertedObjectIDsAfterSearch(algoliaMetadata)
    actions.models.queue.addItem(product, { placement: 'search bar' })
  }, [ query ])

  /**
   * @param {Object} options
   * @param {Object} options.brand
   * @param {string} options.brand.name
   * @param {boolean} options.brand.available
   * @return {void}
   */
  const handleBrandClick = useCallback(({ brand: { name, available, objectID }, position, queryID, indexName }) => {
    const snowplowEvent = available
      ? constants.snowplowEvents.search.brandClick
      : constants.snowplowEvents.search.brandUnavailableClick

    const snowplowMetadata = {
      query,
      brand: name,
    }

    const algoliaMetadata = {
      index: indexName,
      eventName: constants.snowplowEvents.search.brandClick,
      queryID,
      objectIDs: [ objectID ],
      positions: [ position ],
    }

    // algoliaAnalytics.clickedObjectIDsAfterSearch(algoliaMetadata)
    snowplow.track(snowplowEvent, snowplowMetadata)

    sendQueryAnalyticsEvent()
    handleHide()
  }, [ query ])

  /**
   * @param {Object} options
   * @param {Object} options.note
   * @param {string} options.note.name
   * @return {void}
   */
  const handleNoteClick = useCallback(({ note: { name, objectID }, position, queryID, indexName }) => {
    const snowplowEvent = constants.snowplowEvents.search.noteClick

    const snowplowMetadata = {
      query,
      note: name,
    }

    const algoliaMetadata = {
      index: indexName,
      eventName: constants.snowplowEvents.search.noteClick,
      queryID,
      objectIDs: [ objectID ],
      positions: [ position ],
    }

    // algoliaAnalytics.clickedObjectIDsAfterSearch(algoliaMetadata)
    snowplow.track(snowplowEvent, snowplowMetadata)
    sendQueryAnalyticsEvent()
    handleHide()

    actions.view.smartSearchPage.clearFilters()
    actions.view.smartSearchPage.toggleActiveNote(name)
  }, [ query ])

  /**
   * @param {Object} options
   * @param {Object} options.page
   * @param {string} options.page.name
   * @return {void}
   */
  const handlePageClick = useCallback(({ page: { name, objectID }, position, queryID, indexName }) => {
    const snowplowEvent = constants.snowplowEvents.search.pageClick

    const snowplowMetadata = {
      query,
      pageName: name,
    }

    const algoliaMetadata = {
      index: indexName,
      eventName: constants.snowplowEvents.search.pageClick,
      queryID,
      objectIDs: [ objectID ],
      positions: [ position ],
    }

    // algoliaAnalytics.clickedObjectIDsAfterSearch(algoliaMetadata)
    snowplow.track(snowplowEvent, snowplowMetadata)
    sendQueryAnalyticsEvent()
    handleHide()
  }, [ query ])

  /**
   * @param {Object} options
   * @param {Object} options.target
   * @param {string} options.target.value
   */
  const handleChange = useCallback(({ target: { value } }) => {
    setQuery(value)
    fetchData(value)
  }, [])

  const rootClassName = cx(s.root, {
    [s.empty]: query.length < 1,
  })

  const progressbarClassName = cx(s.progressbar, {
    [s.loading]: isLoading,
  })

  const dropdownClassName = cx(s.dropdown, {
    [s.open]: isOpened,
    [s.droplet]: isMobile,
  })

  const progressbar = (
    <div className={progressbarClassName} />
  )

  const results = query.length < 1 ? (
    <SearchSuggestions handleSuggestionClick={handleSuggestionClick} />
  ) : (
    <SearchResults
      query={query}
      isFirstLoaded={isFirstLoaded}
      onProductClick={handleProductClick}
      onProductAdd={handleProductAdd}
      onBrandClick={handleBrandClick}
      onNoteClick={handleNoteClick}
      onPageClick={handlePageClick}
    />
  )

  return (
    <div className={rootClassName} ref={rootRef}>
      <SearchInput
        className={s.input}
        ref={inputRef}
        value={query}
        isOpened={isOpened}
        onChange={handleChange}
        onCancel={handleHide}
        onFocus={handleShow}
      />
      {
        isMobile ? (
          <Fragment>
            {progressbar}
            <WidthContainer className={dropdownClassName}>
              {results}
            </WidthContainer>
          </Fragment>
        ) : (
          <WidthContainer className={s.droplet}>
            {progressbar}
            <Collapse
              className={dropdownClassName}
              isOpened={isOpened}
              springConfig={{
                stiffness: 355,
                damping: 25,
              }}
            >
              {results}
            </Collapse>
          </WidthContainer>
        )
      }
    </div>
  )
}

HeaderAlgoliaSearchInput.propTypes = {
  isMobile: PropTypes.bool, // @injectGlobals
}


export default compose(
  injectGlobals(({ globals }) => ({
    isMobile: globals.device.isMobile,
  })),
)(HeaderAlgoliaSearchInput)
